import { useQuery } from '@tanstack/react-query';
import { router } from 'expo-router';
import { BellOff } from 'lucide-react-native';
import { Platform } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { Box } from '@/components/ui/box';
import { Button, ButtonText } from '@/components/ui/button';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { ROUTES } from '@/constants/routes';
import { getCurrentUserScopedQueryOptions } from '@/services/getCurrentUserScoped';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useAwayScheduleQuery } from '@/hooks/useAwaySchedule';
import { useEndAwayNow } from '@/hooks/useEndAwayNow';
import { useToday } from '@/hooks/useToday';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { getAccessibleHitSlop } from '@/util/accessibleTouchTarget';
import { formatAwayDay, getCurrentAwayBlock } from '@/util/awaySchedule';

/**
 * I'm back is drawn as a 36pt outlined pill so it reads as a button on the amber strip, where
 * an underlined word blended into the sentence beside it. The hit slop brings the target to 48.
 */
const I_AM_BACK_HIT_SLOP = getAccessibleHitSlop(36);

type AwayBannerProps = {
  className?: string;
  contentClassName?: string;
  linkToSettings?: boolean;
  userId?: string;
};

/**
 * Slim amber status strip shown to experts whose availability is set to "away".
 * Amber marks a self-chosen "inquiries are paused" attention state, kept
 * distinct from the promotional announcement banner. Changing dates stays in
 * account settings, but with the away calendar on, the banner offers the one
 * action an away expert most often wants, I'm back, which ends the time away
 * covering today in one tap, with no confirmation or Undo. With I'm back offered, the
 * copy is cut to one short line and the settings link is dropped, since two actions and a
 * sentence crowded the inbox; dates are still changed from the avatar's Account screen.
 * `linkToSettings` turns the "account settings" phrase into a
 * link to the account screen; surfaces that already are the account screen leave
 * it off so the banner does not link to itself. The surface is full-bleed while
 * its content sits in a centered max-width row; `contentClassName` sets that
 * row's width and responsive padding so it aligns with the host surface's own
 * content column (wider on inbox to match the announcement, narrower on the
 * account settings column).
 *
 * Inbox used to pass a `border-t` seam through `className` for the case where
 * this stacked directly under the web announcement, back when that announcement
 * was a full-bleed band immediately above. The announcement is now an inset card
 * rendered from the secure layout, nowhere near this, so the seam separated this
 * banner from whatever happened to precede it. `className` stays part of the
 * API; nothing passes a seam through it today.
 */
export function AwayBanner(props: AwayBannerProps) {
  const isCalendarEnabled = useFeatureFlag(FEATURE_FLAGS.EXPERT_AWAY_CALENDAR);

  return isCalendarEnabled ? <ScheduledAwayBanner {...props} /> : <LegacyAwayBanner {...props} />;
}

/**
 * The banner as it was before away calendars: it follows the Away status alone,
 * says nothing about dates, and reads no away schedule.
 */
function LegacyAwayBanner(props: AwayBannerProps) {
  const { data } = useQuery(getCurrentUserScopedQueryOptions({ userId: props.userId }));

  return (
    <AwayBannerView {...props} isAway={data?.status === '2'} throughDate={null} isLegacyCopy />
  );
}

/** The banner with the calendar on: also shows while a block covers today, and names its last day. */
function ScheduledAwayBanner(props: AwayBannerProps) {
  const { data } = useQuery(getCurrentUserScopedQueryOptions({ userId: props.userId }));

  const today = useToday();

  const { data: blocks = [] } = useAwayScheduleQuery({ enabled: data?.userType === 'expert' });

  const currentBlock = getCurrentAwayBlock(blocks, today);
  const awayThrough = currentBlock?.endDate ?? null;
  const { endAwayNow, isEnding } = useEndAwayNow(props.userId);

  // A status set through the old switch has no end date, so it shows the banner
  // without one and takes precedence over a block.
  const statusIsAway = data?.status === '2';
  const isAway = statusIsAway || awayThrough !== null;
  const throughDate = statusIsAway || !awayThrough ? null : formatAwayDay(awayThrough, today);

  return (
    <AwayBannerView
      {...props}
      isAway={isAway}
      throughDate={throughDate}
      isEnding={isEnding}
      onEndAway={() => endAwayNow({ block: currentBlock, legacyAway: statusIsAway, today })}
    />
  );
}

function AwayBannerView({
  className = '',
  contentClassName = 'max-w-screen-xl lg:px-10',
  linkToSettings = false,
  isAway,
  throughDate,
  isLegacyCopy = false,
  onEndAway,
  isEnding = false,
}: AwayBannerProps & {
  isAway: boolean;
  throughDate: string | null;
  /** Ends the time away covering today. Only the calendar banner offers it. */
  onEndAway?: () => void;
  isEnding?: boolean;
  /** The wording from before away calendars, kept with the flag off so nothing changes for experts on the old switch. */
  isLegacyCopy?: boolean;
}) {
  const goToSettings = () => router.navigate(ROUTES.ACCOUNT);

  /**
   * On web, `onPress` makes RN-Web render a block-level `<button>` that breaks
   * the surrounding inline text flow and swallows the press; passing `onClick`
   * on the underlying DOM node keeps the link inline and clickable. Native uses
   * `onPress`, where a nested `Text` stays inline and fires correctly.
   */
  const linkPressProps =
    Platform.OS === 'web' ? ({ onClick: goToSettings } as object) : { onPress: goToSettings };

  return isAway ? (
    <Box className={twJoin('bg-background-warning', className)}>
      <HStack className={twJoin('mx-auto w-full items-start gap-2 px-5 py-2', contentClassName)}>
        <Icon as={BellOff} size="sm" className="mt-0.5 shrink-0 text-warning-800" />

        {isLegacyCopy ? (
          <Text size="sm" className="min-w-0 flex-1 text-warning-800">
            You are set as away. While away, you won't be sent new inquiries. Availability can be
            updated in your{' '}
            {linkToSettings ? (
              <Text
                size="sm"
                className="font-medium text-warning-800 underline web:cursor-pointer"
                {...linkPressProps}
                {...getAccessibilityProps({
                  role: 'link',
                  label: 'account settings',
                })}>
                account settings
              </Text>
            ) : (
              'account settings'
            )}
            .
          </Text>
        ) : (
          <Text size="sm" className="min-w-0 flex-1 text-warning-800">
            Away{throughDate ? ` through ${throughDate}` : ''}. No new inquiries.
            {linkToSettings && !onEndAway ? (
              <>
                {' '}
                <Text
                  size="sm"
                  className="font-medium text-warning-800 underline web:cursor-pointer"
                  {...linkPressProps}
                  {...getAccessibilityProps({ role: 'link', label: 'Change in Account' })}>
                  Change in Account
                </Text>
              </>
            ) : null}
          </Text>
        )}

        {onEndAway ? (
          <Button
            variant="outline"
            action="warning"
            size="sm"
            className="h-9 shrink-0 self-center px-4"
            hitSlop={I_AM_BACK_HIT_SLOP}
            isDisabled={isEnding}
            onPress={onEndAway}
            {...getAccessibilityProps({
              role: 'button',
              label: 'I’m back',
              hint: 'Ends your time away now. You can undo it right after.',
            })}>
            <ButtonText size="sm">I’m back</ButtonText>
          </Button>
        ) : null}
      </HStack>
    </Box>
  ) : null;
}
